import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { ArrowForwardRounded, IosShare, KeyboardArrowDownRounded, KeyboardArrowUpRounded } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';

import ChatV2Components from '../components/ChatV2Components';
import ChatAssistantTurn from '../components/ChatAssistantTurn';
import ChatRequestFailure from '../components/ChatRequestFailure';
import ChatThreadLoading from '../components/ChatThreadLoading';
import PublicChatThreadLoading from '../components/PublicChatThreadLoading';
import {
    CHAT_V2_MAX_INPUT_CHARACTERS,
    getChatV2ErrorMessage,
    isChatV2RunActive,
    isChatV2RunTerminal,
    normalizeChatV2Run,
} from '../services/chatV2';
import {
    getVisibleChatV2ExploreNextSuggestions,
    normalizeChatV2TopicKey,
    resolveChatV2ExploreNextSuggestions,
} from '../utils/chatV2TopicUtils';
import { getChatAdapter, invalidateChatSuggestions } from '../services/chatThreads';
import { buildMissingRoute } from '../utils/missingRoute';
import { chatRequestFailed, chatRequestResponseText } from '../utils/chatRequestFailure';
import {
    addAppScrollListener,
    getAppElementScrollTop,
    getAppScrollElement,
    getAppScrollTop,
    getAppViewport,
    scrollAppTo,
} from '../utils/appViewport';

const MAX_HEIGHT = 240;
const FLOATING_NAV_SCROLL_THRESHOLD = 100;
const MESSAGE_SCROLL_TOP_OFFSET = 92;
const MESSAGE_NAV_TOLERANCE = 20;
const BOTTOM_OVERLAY_GAP = 16;
const PUBLIC_BOTTOM_OVERLAY_GAP = 12;
const TOP_SCROLL_TOLERANCE = 4;
const BOTTOM_SCROLL_TOLERANCE = 4;
const CONTENT_BOUNCE_DURATION_MS = 420;
const buildPendingUserMessage = ({ runId, text }) => {
    const createdAt = new Date().toISOString();

    return {
        id: `pending_${runId}`,
        role: 'user',
        text,
        sections: [],
        exploreNextTopics: [],
        exploreNextSuggestions: [],
        runId,
        createdAt,
        updatedAt: createdAt,
        pending: true,
    };
};

const hasAssistantMessageForRun = (messages, runId) => Array.isArray(messages) && messages.some((message) => message.role === 'assistant' && message.runId === runId);

const hasAssistantResponseContent = (entry) => {
    if (!entry || typeof entry !== 'object') {
        return false;
    }

    if (typeof entry.text === 'string' && entry.text.trim()) {
        return true;
    }

    if (chatRequestResponseText(entry)) {
        return true;
    }

    if (Array.isArray(entry.sections) && entry.sections.some((section) => {
        return section && Array.isArray(section.components) && section.components.length > 0;
    })) {
        return true;
    }

    return resolveChatV2ExploreNextSuggestions({
        exploreNextSuggestions: entry.exploreNextSuggestions,
        exploreNextTopics: entry.exploreNextTopics,
    }).length > 0;
};

const hasAssistantBodyContent = (entry) => {
    if (!entry || typeof entry !== 'object') {
        return false;
    }

    if (typeof entry.text === 'string' && entry.text.trim()) {
        return true;
    }

    if (chatRequestResponseText(entry)) {
        return true;
    }

    return Array.isArray(entry.sections) && entry.sections.some((section) => {
        return section && Array.isArray(section.components) && section.components.length > 0;
    });
};

const shouldRenderLiveRun = (messages, liveRun) => {
    if (!liveRun || !liveRun.runId) {
        return false;
    }

    if (isChatV2RunActive(liveRun) || liveRun.status === 'failed') {
        return true;
    }

    return !hasAssistantMessageForRun(messages, liveRun.runId);
};

const sortMessages = (messages) => [...messages].sort((left, right) => {
    const leftTime = Date.parse(left.createdAt || 0);
    const rightTime = Date.parse(right.createdAt || 0);

    return leftTime - rightTime;
});

const getOverlaySafeBottomPadding = (bottomOverlayHeight, gap = BOTTOM_OVERLAY_GAP) => bottomOverlayHeight + gap;

const getExploreNextAnchorExtraPadding = ({ currentExtraPadding, targetNode }) => {
    if (typeof window === 'undefined' || !targetNode) {
        return 0;
    }

    const scrollingElement = getAppScrollElement();

    if (!scrollingElement) {
        return 0;
    }

    const targetAbsoluteTop = getAppElementScrollTop(targetNode);
    const desiredScrollTop = Math.max(0, targetAbsoluteTop - MESSAGE_SCROLL_TOP_OFFSET);
    const scrollHeightWithoutExtraPadding = scrollingElement.scrollHeight - currentExtraPadding;
    const maxScrollTopWithoutExtraPadding = Math.max(0, scrollHeightWithoutExtraPadding - getAppViewport().height);

    return Math.max(0, desiredScrollTop - maxScrollTopWithoutExtraPadding);
};

const ChatThreadScreen = ({ publicView = false, engineVersion = 2 }) => {
    const chat = getChatAdapter(engineVersion);
    const analytics = getAnalytics();
    const navigate = useNavigate();
    const location = useLocation();
    const missingRoute = buildMissingRoute(location);
    const navigationType = useNavigationType();
    const params = useParams();
    const threadId = publicView ? params.sharedThreadId : params.threadId;
    const hasFreshInitialRunState = !publicView && navigationType === 'PUSH';
    const initialRun = hasFreshInitialRunState && location.state && location.state.initialRun ? location.state.initialRun : null;
    const initialQuery = hasFreshInitialRunState && location.state && location.state.initialQuery ? location.state.initialQuery : '';
    const textareaRef = useRef(null);
    const mountedRef = useRef(true);
    const submittingRef = useRef(false);
    const rejectedSendRunRef = useRef(null);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const syncedTerminalRunsRef = useRef(new Set());
    const userMessageRefsRef = useRef({});
    const messageRefsRef = useRef({});
    const contentRef = useRef(null);
    const composerOverlayRef = useRef(null);
    const bottomControlsOverlayRef = useRef(null);
    const initialScrollRunIdRef = useRef(initialRun && initialQuery ? initialRun.runId : null);
    const exploreNextScrollRunIdRef = useRef(null);
    const contentBounceAnimationFrameRef = useRef(null);
    const contentBounceTimeoutRef = useRef(null);

    const [threadResponse, setThreadResponse] = useState(null);
    const [liveRun, setLiveRun] = useState(initialRun);
    const [pendingMessages, setPendingMessages] = useState(() => {
        if (!initialRun || !initialQuery) {
            return [];
        }

        return [buildPendingUserMessage({ runId: initialRun.runId, text: initialQuery })];
    });
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(!initialRun);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [focusComposer, setFocusComposer] = useState(false);
    const [copyNotificationMessage, setCopyNotificationMessage] = useState('');
    const [popupMessage, setPopupMessage] = useState('');
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [unsharing, setUnsharing] = useState(false);
    const [showFloatingMessageNav, setShowFloatingMessageNav] = useState(false);
    const [{ canScrollUp, canScrollDown }, setScrollAvailability] = useState({
        canScrollUp: false,
        canScrollDown: false,
    });
    const [bottomOverlayHeight, setBottomOverlayHeight] = useState(0);
    const [composerDockHeight, setComposerDockHeight] = useState(0);
    const [topAnchoredExploreNextRunId, setTopAnchoredExploreNextRunId] = useState(null);
    const [exploreNextAnchorExtraPadding, setExploreNextAnchorExtraPadding] = useState(0);
    const [contentBounceDirection, setContentBounceDirection] = useState('');

    const messages = useMemo(() => threadResponse && Array.isArray(threadResponse.messages) ? threadResponse.messages : [], [threadResponse]);
    const latestRun = useMemo(() => threadResponse && threadResponse.latestRun ? threadResponse.latestRun : null, [threadResponse]);
    const ownedThreadIsShared = Boolean(!publicView && threadResponse && threadResponse.thread && threadResponse.thread.isShared);
    const currentRun = liveRun || latestRun || null;
    const currentRunId = currentRun?.runId;
    const currentRunIsActive = isChatV2RunActive(currentRun);

    const displayedMessages = useMemo(() => {
        const persistedRunIds = new Set(messages.filter((message) => message.role === 'user' && message.runId).map((message) => message.runId));
        const unpersistedPendingMessages = pendingMessages.filter((message) => !persistedRunIds.has(message.runId));

        return sortMessages(messages.concat(unpersistedPendingMessages)).map((message) => {
            if (message.role !== 'assistant' || message.runId !== currentRun?.runId || !chatRequestFailed(currentRun)) {
                return message;
            }
            return { ...message, text: chatRequestResponseText({ ...currentRun, assistantText: message.text }) };
        });
    }, [currentRun, messages, pendingMessages]);
    const userMessageIndexes = useMemo(() => displayedMessages.reduce((indexes, message, index) => {
        if (message.role === 'user') {
            indexes.push(index);
        }

        return indexes;
    }, []), [displayedMessages]);
    const exploredTopicKeys = useMemo(() => {
        return new Set(displayedMessages
            .filter((message) => message.role === 'user')
            .map((message) => normalizeChatV2TopicKey(message.text))
            .filter(Boolean));
    }, [displayedMessages]);

    const hasLoadedInitialThread = threadResponse !== null;
    const showLiveRunMessage = shouldRenderLiveRun(messages, currentRun);
    const liveRunResponseIsSaved = Boolean(currentRun?.runId && hasAssistantMessageForRun(messages, currentRun.runId));
    const threadHasResponse = useMemo(() => {
        if (displayedMessages.some((message) => message.role === 'assistant' && hasAssistantResponseContent(message))) {
            return true;
        }

        return showLiveRunMessage && hasAssistantResponseContent(currentRun);
    }, [currentRun, displayedMessages, showLiveRunMessage]);
    const threadHasFinishedResponse = useMemo(() => {
        if (displayedMessages.some((message) => message.role === 'assistant' && hasAssistantResponseContent(message))) {
            return true;
        }

        return Boolean(
            currentRun &&
            currentRun.status === 'complete' &&
            showLiveRunMessage &&
            hasAssistantResponseContent(currentRun),
        );
    }, [currentRun, displayedMessages, showLiveRunMessage]);
    const shouldShowMessageNavigation = hasLoadedInitialThread &&
        (showFloatingMessageNav || threadHasFinishedResponse);
    const currentRunFailed = chatRequestFailed(currentRun);
    const runIsLoading = submitting || (isChatV2RunActive(currentRun) && !currentRunFailed);
    const composerDisabled = runIsLoading;
    const sendDisabled = submitting || isChatV2RunActive(currentRun);
    const showComposer = !publicView && (threadHasResponse || currentRunFailed);
    const exploreNextTopicsDisabled = publicView ? false : sendDisabled;
    const hasText = text.trim().length > 0;
    const overlaySafeBottomPadding = getOverlaySafeBottomPadding(
        bottomOverlayHeight,
        publicView ? PUBLIC_BOTTOM_OVERLAY_GAP : BOTTOM_OVERLAY_GAP,
    );
    // Before the public dock is measured: 44px controls + 16px bottom offset + 12px gap.
    const minimumContentBottomPadding = publicView ? 72 : 0;
    const baseContentBottomPadding = Math.max(overlaySafeBottomPadding, minimumContentBottomPadding);
    const shouldKeepExploreNextTopPadding = Boolean(
        topAnchoredExploreNextRunId &&
        currentRun &&
        currentRun.runId === topAnchoredExploreNextRunId &&
        !hasAssistantMessageForRun(messages, topAnchoredExploreNextRunId),
    );
    const contentBottomPadding = `${baseContentBottomPadding + exploreNextAnchorExtraPadding}px`;

    const syncTextareaHeight = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;

        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
    }, []);

    const updateBottomOverlayHeight = useCallback(() => {
        const composerDockNode = composerOverlayRef.current;
        const overlayTop = [composerDockNode, bottomControlsOverlayRef.current]
            .filter(Boolean)
            .map((node) => node.getBoundingClientRect().top);
        const nextComposerDockHeight = composerDockNode ? composerDockNode.getBoundingClientRect().height : 0;

        const nextBottomOverlayHeight = overlayTop.length > 0 ? Math.max(0, getAppViewport().bottom - Math.min(...overlayTop)) : 0;

        setComposerDockHeight((previousHeight) => Math.abs(previousHeight - nextComposerDockHeight) < 1 ? previousHeight : nextComposerDockHeight);
        setBottomOverlayHeight((previousHeight) => Math.abs(previousHeight - nextBottomOverlayHeight) < 1 ? previousHeight : nextBottomOverlayHeight);
    }, []);

    useEffect(() => {
        const audio = document.getElementById('audio');
        audio.pause();
        logEvent(analytics, 'screen_view', {
            firebase_screen: publicView ? 'public_chat_thread' : 'chat_thread'
        });
    }, [analytics, publicView]);

    useEffect(() => {
        syncTextareaHeight();
    }, [text, syncTextareaHeight]);

    useEffect(() => {
        const rejectedRun = rejectedSendRunRef.current;
        if (!errorMessage || !rejectedRun) return;

        if (rejectedRun.runId !== (currentRun?.runId || null) || (rejectedRun.failed && !currentRunFailed)) {
            rejectedSendRunRef.current = null;
            setErrorMessage('');
        }
    }, [currentRun?.runId, currentRunFailed, errorMessage]);

    useEffect(() => {
        if (focusComposer && showComposer && textareaRef.current) {
            textareaRef.current.focus();
            setFocusComposer(false);
        }
    }, [focusComposer, showComposer]);

    useEffect(() => {
        if (!copyNotificationMessage) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setCopyNotificationMessage('');
        }, 1800);

        return () => window.clearTimeout(timeoutId);
    }, [copyNotificationMessage]);

    useEffect(() => {
        updateBottomOverlayHeight();

        const animationFrameId = window.requestAnimationFrame(updateBottomOverlayHeight);
        const overlayNodes = [composerOverlayRef.current, bottomControlsOverlayRef.current].filter(Boolean);
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
            updateBottomOverlayHeight();
        });

        overlayNodes.forEach((node) => resizeObserver?.observe(node));
        window.addEventListener('resize', updateBottomOverlayHeight);

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateBottomOverlayHeight);
        };
    }, [runIsLoading, shouldShowMessageNavigation, text, showComposer, threadHasResponse, updateBottomOverlayHeight]);

    useEffect(() => {
        const syncFloatingMessageNav = () => {
            setShowFloatingMessageNav((previousValue) => {
                const nextValue = getAppScrollTop() > FLOATING_NAV_SCROLL_THRESHOLD;
                return previousValue === nextValue ? previousValue : nextValue;
            });

            const maxScrollTop = Math.max(0, (getAppScrollElement()?.scrollHeight || 0) - getAppViewport().height);
            const scrollTop = Math.max(0, getAppScrollTop());
            const nextCanScrollUp = maxScrollTop > TOP_SCROLL_TOLERANCE && scrollTop > TOP_SCROLL_TOLERANCE;
            const nextCanScrollDown = maxScrollTop - scrollTop > BOTTOM_SCROLL_TOLERANCE;

            setScrollAvailability((previous) => (
                previous.canScrollUp === nextCanScrollUp && previous.canScrollDown === nextCanScrollDown ?
                    previous :
                    { canScrollUp: nextCanScrollUp, canScrollDown: nextCanScrollDown }
            ));
        };

        syncFloatingMessageNav();
        const removeScrollListener = addAppScrollListener(syncFloatingMessageNav);
        window.addEventListener('resize', syncFloatingMessageNav);

        // Results, artwork, and expanded suggestions can change the page height without a scroll.
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncFloatingMessageNav);
        if (contentRef.current) resizeObserver?.observe(contentRef.current, { box: 'border-box' });

        return () => {
            removeScrollListener();
            window.removeEventListener('resize', syncFloatingMessageNav);
            resizeObserver?.disconnect();
        };
    }, [hasLoadedInitialThread, threadId]);

    useEffect(() => {
        const pendingRunId = initialScrollRunIdRef.current;

        if (!pendingRunId) {
            return;
        }

        const targetNode = userMessageRefsRef.current[pendingRunId];

        if (!targetNode) {
            return;
        }

        initialScrollRunIdRef.current = null;
        window.requestAnimationFrame(() => {
            targetNode.scrollIntoView({ block: 'start' });
        });
    }, [displayedMessages]);

    useEffect(() => {
        return () => {
            if (contentBounceAnimationFrameRef.current) {
                window.cancelAnimationFrame(contentBounceAnimationFrameRef.current);
            }

            if (contentBounceTimeoutRef.current) {
                window.clearTimeout(contentBounceTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const pendingRunId = exploreNextScrollRunIdRef.current;

        if (!pendingRunId) {
            return;
        }

        const targetNode = userMessageRefsRef.current[pendingRunId];

        if (!targetNode) {
            return;
        }

        exploreNextScrollRunIdRef.current = null;
        window.requestAnimationFrame(() => {
            targetNode.scrollIntoView({ block: 'start' });
        });
    }, [bottomOverlayHeight, displayedMessages]);

    useEffect(() => {
        if (!topAnchoredExploreNextRunId) {
            setExploreNextAnchorExtraPadding((previousPadding) => previousPadding === 0 ? previousPadding : 0);
            return;
        }

        if (hasAssistantMessageForRun(messages, topAnchoredExploreNextRunId)) {
            setTopAnchoredExploreNextRunId(null);
            return;
        }

        if (currentRun && currentRun.runId && currentRun.runId !== topAnchoredExploreNextRunId) {
            setTopAnchoredExploreNextRunId(null);
        }
    }, [currentRun, messages, topAnchoredExploreNextRunId]);

    useEffect(() => {
        if (!shouldKeepExploreNextTopPadding) {
            return;
        }

        const targetNode = userMessageRefsRef.current[topAnchoredExploreNextRunId];

        if (!targetNode) {
            return;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            const nextExtraPadding = getExploreNextAnchorExtraPadding({
                currentExtraPadding: exploreNextAnchorExtraPadding,
                targetNode,
            });

            setExploreNextAnchorExtraPadding((previousPadding) => {
                return Math.abs(previousPadding - nextExtraPadding) < 1 ? previousPadding : nextExtraPadding;
            });

            if (Math.abs(targetNode.getBoundingClientRect().top - getAppViewport().top - MESSAGE_SCROLL_TOP_OFFSET) > MESSAGE_NAV_TOLERANCE) {
                targetNode.scrollIntoView({ block: 'start' });
            }
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [bottomOverlayHeight, currentRun, displayedMessages, exploreNextAnchorExtraPadding, shouldKeepExploreNextTopPadding, topAnchoredExploreNextRunId]);

    useEffect(() => {
        if (!shouldKeepExploreNextTopPadding) {
            return;
        }

        const clearExploreNextAnchor = () => {
            setTopAnchoredExploreNextRunId(null);
        };

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'PageDown' || e.key === 'PageUp' || e.key === 'Home' || e.key === 'End' || e.key === ' ') {
                clearExploreNextAnchor();
            }
        };

        window.addEventListener('wheel', clearExploreNextAnchor, { passive: true });
        window.addEventListener('touchmove', clearExploreNextAnchor, { passive: true });
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('wheel', clearExploreNextAnchor);
            window.removeEventListener('touchmove', clearExploreNextAnchor);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [shouldKeepExploreNextTopPadding]);

    const loadThread = useCallback(async () => {
        if (!threadId) {
            navigate(missingRoute);
            return;
        }

        try {
            setLoading(true);
            const response = publicView ? await chat.getSharedThread(threadId) : await chat.getThread(threadId);
            if (!mountedRef.current) return;
            setThreadResponse(response);
            const responseMessages = Array.isArray(response.messages) ? response.messages : [];
            setPendingMessages((previousMessages) => previousMessages.filter((pendingMessage) => {
                return !responseMessages.some((message) => message.role === 'user' && message.runId === pendingMessage.runId);
            }));
            if (!publicView && response.latestRun) {
                setLiveRun((previousRun) => {
                    if (!previousRun) {
                        return response.latestRun;
                    }

                    if (previousRun.runId !== response.latestRun.runId) {
                        return response.latestRun;
                    }

                    if (previousRun.status !== response.latestRun.status && isChatV2RunTerminal(response.latestRun)) {
                        return response.latestRun;
                    }

                    return previousRun;
                });
            }
        } catch (err) {
            if (!mountedRef.current) return;
            console.log(err);
            navigate(missingRoute);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [chat, missingRoute, navigate, publicView, threadId]);

    useEffect(() => {
        loadThread();
    }, [loadThread]);

    useEffect(() => {
        if (publicView || !threadId || !currentRunId || !currentRunIsActive) {
            return;
        }

        const publicStatusRef = chat.getStatusRef(threadId, currentRunId);
        let active = true;

        const unsubscribe = publicStatusRef.onSnapshot((snapshot) => {
            if (!active || !snapshot.exists) {
                return;
            }

            setLiveRun(normalizeChatV2Run(currentRunId, snapshot.data()));
        }, (err) => {
            console.log(err);
        });

        return () => {
            active = false;
            unsubscribe();
        };
    }, [chat, currentRunId, currentRunIsActive, publicView, threadId]);

    useEffect(() => {
        if (publicView || !currentRun || !currentRun.runId || !isChatV2RunTerminal(currentRun)) {
            return;
        }

        if (syncedTerminalRunsRef.current.has(currentRun.runId)) {
            return;
        }

        syncedTerminalRunsRef.current.add(currentRun.runId);
        if (currentRun.status === 'complete') invalidateChatSuggestions({ afterPublication: true });
        loadThread();
    }, [currentRun, loadThread, publicView]);

    const submitQuery = useCallback(async (rawQuery) => {
        const query = String(rawQuery || '').trim();

        if (publicView || !threadId || !query || sendDisabled || submittingRef.current) {
            return false;
        }

        submittingRef.current = true;
        setSubmitting(true);
        setErrorMessage('');

        try {
            const response = await chat.sendMessage(threadId, query);
            if (!mountedRef.current) return false;
            const optimisticUserMessage = buildPendingUserMessage({ runId: response.runId, text: query });

            exploreNextScrollRunIdRef.current = response.runId;
            setExploreNextAnchorExtraPadding(0);
            setTopAnchoredExploreNextRunId(response.runId);
            syncedTerminalRunsRef.current.delete(response.runId);
            setPendingMessages((previousMessages) => previousMessages.concat(optimisticUserMessage));
            setThreadResponse((previousResponse) => previousResponse ? {
                ...previousResponse,
                latestRun: response,
                thread: previousResponse.thread ? {
                    ...previousResponse.thread,
                    latestRunId: response.runId,
                    latestRunStatus: response.status,
                } : previousResponse.thread,
            } : previousResponse);
            setLiveRun(response);
            setText('');
            return true;
        } catch (err) {
            if (!mountedRef.current) return false;
            console.log(err);
            rejectedSendRunRef.current = { runId: currentRun?.runId || null, failed: currentRunFailed };
            setErrorMessage(
                getChatV2ErrorMessage(
                    err && err.response && err.response.data
                        ? err.response.data.error
                        : err,
                ) || 'Unable to send message.',
            );
            setText((draft) => draft.trim() ? draft : query);

            if (err && err.response && err.response.status === 409) {
                loadThread();
            }

            return false;
        } finally {
            submittingRef.current = false;
            if (mountedRef.current) setSubmitting(false);
        }
    }, [chat, currentRun?.runId, currentRunFailed, sendDisabled, loadThread, publicView, threadId]);

    const submitMessage = useCallback(async (e) => {
        e.preventDefault();
        await submitQuery(text);
    }, [submitQuery, text]);

    const handleComposerKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            if (!sendDisabled && hasText) {
                void submitQuery(text);
            }
        }
    }, [sendDisabled, hasText, submitQuery, text]);

    const handleComposerChange = useCallback((e) => {
        if (errorMessage) {
            setErrorMessage('');
        }

        setText(e.target.value.slice(0, CHAT_V2_MAX_INPUT_CHARACTERS));
    }, [errorMessage]);

    const failedRequest = currentRunFailed ? displayedMessages.find((message) => (
        message.role === 'user' && message.runId === currentRun?.runId && message.text?.trim()
    ))?.text : '';
    const editFailedRequest = () => {
        if (!failedRequest || composerDisabled) return;
        setText((draft) => draft.trim() ? draft : failedRequest);
        setErrorMessage('');
        setFocusComposer(true);
    };

    const handleExploreNextTopic = useCallback((topic) => {
        const nextTopic = String(topic || '').trim();

        if (!nextTopic) {
            return;
        }

        if (publicView) {
            const searchParams = new URLSearchParams();
            searchParams.set('q', nextTopic);
            navigate(`/home?${searchParams.toString()}`);
            return;
        }

        void submitQuery(nextTopic);
    }, [navigate, publicView, submitQuery]);

    const isTopicAlreadyExplored = useCallback((topic) => {
        return exploredTopicKeys.has(normalizeChatV2TopicKey(topic));
    }, [exploredTopicKeys]);

    const copyUserMessageToClipboard = useCallback(async (messageText) => {
        const nextMessageText = String(messageText || '').trim();

        if (!nextMessageText) {
            return;
        }

        if (!navigator.clipboard || !navigator.clipboard.writeText) {
            setCopyNotificationMessage('Copying messages is not supported on this device.');
            return;
        }

        try {
            await navigator.clipboard.writeText(nextMessageText);
            setCopyNotificationMessage('Message copied to clipboard!');
        } catch (copyErr) {
            console.log(copyErr);
            setCopyNotificationMessage('Copying the message failed.');
        }
    }, []);

    const copyShareUrlToClipboard = useCallback(async (shareUrl, {
        successMessage,
        unsupportedMessage,
        failureMessage,
    }) => {
        if (!navigator.clipboard || !navigator.clipboard.writeText) {
            setPopupMessage(unsupportedMessage);
            return;
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            setPopupMessage(successMessage);
        } catch (copyErr) {
            console.log(copyErr);
            setPopupMessage(failureMessage);
        }
    }, []);

    const createOrUpdateShareLink = useCallback(async () => {
        if (!threadId || sharing) {
            return;
        }

        if (!publicView) {
            setShareDialogOpen(false);
        }
        setSharing(true);
        setPopupMessage('');

        try {
            if (publicView) {
                const shareUrl = `${window.location.origin}${chat.publicPath(threadId)}`;

                await copyShareUrlToClipboard(shareUrl, {
                    successMessage: 'Link copied to clipboard!',
                    unsupportedMessage: 'Copying the link is not supported on this device.',
                    failureMessage: 'Copying the link failed.',
                });
                return;
            }

            const response = await chat.share(threadId);
            const sharePath = response && response.sharePath ? response.sharePath : chat.publicPath(threadId);
            const shareUrl = `${window.location.origin}${sharePath}`;
            setThreadResponse((previousResponse) => previousResponse && previousResponse.thread ? {
                ...previousResponse,
                thread: {
                    ...previousResponse.thread,
                    isShared: true,
                    sharePath,
                    sharedAt: response && response.sharedAt ? response.sharedAt : previousResponse.thread.sharedAt || new Date().toISOString(),
                },
            } : previousResponse);

            await copyShareUrlToClipboard(shareUrl, {
                successMessage: 'Link copied to clipboard!',
                unsupportedMessage: 'Thread shared, but copying the link is not supported on this device.',
                failureMessage: 'Thread shared, but copying the link failed.',
            });
        } catch (err) {
            console.log(err);
            const status = err && err.response ? err.response.status : null;
            if (status === 401 || status === 403 || status === 404) {
                setPopupMessage('You no longer have access to share this conversation.');
                return;
            }

            setPopupMessage(getChatV2ErrorMessage(err && err.response && err.response.data && err.response.data.error) || 'Unable to share this thread.');
        } finally {
            setSharing(false);
        }
    }, [chat, copyShareUrlToClipboard, publicView, sharing, threadId]);

    const shareButtonClick = useCallback(() => {
        if (publicView) {
            void createOrUpdateShareLink();
            return;
        }

        setShareDialogOpen(true);
    }, [createOrUpdateShareLink, publicView]);

    const stopSharing = useCallback(async () => {
        if (!threadId || unsharing) {
            return;
        }

        setShareDialogOpen(false);
        setUnsharing(true);
        setPopupMessage('');

        try {
            const response = await chat.unshare(threadId);
            setThreadResponse((previousResponse) => previousResponse && previousResponse.thread ? {
                ...previousResponse,
                thread: {
                    ...previousResponse.thread,
                    isShared: false,
                    sharePath: response && response.sharePath ? response.sharePath : previousResponse.thread.sharePath,
                    sharedAt: null,
                },
            } : previousResponse);
            setPopupMessage('Sharing stopped. The link no longer works.');
        } catch (err) {
            console.log(err);
            const status = err && err.response ? err.response.status : null;
            if (status === 401 || status === 403 || status === 404) {
                setPopupMessage('You no longer have access to stop sharing this conversation.');
                return;
            }

            setPopupMessage(getChatV2ErrorMessage(err && err.response && err.response.data && err.response.data.error) || 'Unable to stop sharing this thread.');
        } finally {
            setUnsharing(false);
        }
    }, [chat, threadId, unsharing]);

    const bindMessageNode = (messageIndex, runId = null) => (node) => {
        if (node) {
            messageRefsRef.current[messageIndex] = node;

            if (runId) {
                userMessageRefsRef.current[runId] = node;
            }

            return;
        }

        delete messageRefsRef.current[messageIndex];

        if (runId) {
            delete userMessageRefsRef.current[runId];
        }
    };

    const getCurrentUserMessagePosition = useCallback(() => {
        if (userMessageIndexes.length === 0) {
            return -1;
        }

        const anchorY = getAppViewport().top + MESSAGE_SCROLL_TOP_OFFSET + 12;
        let fallbackPosition = -1;

        for (let position = 0; position < userMessageIndexes.length; position += 1) {
            const node = messageRefsRef.current[userMessageIndexes[position]];

            if (!node) {
                continue;
            }

            const rect = node.getBoundingClientRect();

            if (rect.top <= anchorY && rect.bottom >= anchorY) {
                return position;
            }

            if (rect.top < anchorY) {
                fallbackPosition = position;
                continue;
            }

            return fallbackPosition >= 0 ? fallbackPosition : position;
        }

        return fallbackPosition >= 0 ? fallbackPosition : 0;
    }, [userMessageIndexes]);

    const scrollMessageIntoView = useCallback((messageIndex) => {
        messageRefsRef.current[messageIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const triggerContentBounce = useCallback((direction) => {
        if (typeof window === 'undefined') {
            return;
        }

        if (contentBounceTimeoutRef.current) {
            window.clearTimeout(contentBounceTimeoutRef.current);
        }

        if (contentBounceAnimationFrameRef.current) {
            window.cancelAnimationFrame(contentBounceAnimationFrameRef.current);
        }

        setContentBounceDirection('');

        contentBounceAnimationFrameRef.current = window.requestAnimationFrame(() => {
            contentBounceAnimationFrameRef.current = null;
            setContentBounceDirection(direction);
            contentBounceTimeoutRef.current = window.setTimeout(() => {
                setContentBounceDirection('');
                contentBounceTimeoutRef.current = null;
            }, CONTENT_BOUNCE_DURATION_MS);
        });
    }, []);

    const handleScrollToMessageTop = useCallback(() => {
        setTopAnchoredExploreNextRunId(null);

        if (getAppScrollTop() <= TOP_SCROLL_TOLERANCE) {
            triggerContentBounce('top');
            return;
        }

        const currentUserMessagePosition = getCurrentUserMessagePosition();

        if (currentUserMessagePosition < 0) {
            return;
        }

        const currentMessageIndex = userMessageIndexes[currentUserMessagePosition];
        const currentNode = messageRefsRef.current[currentMessageIndex];

        if (!currentNode) {
            return;
        }

        const isAtCurrentMessageTop = Math.abs(currentNode.getBoundingClientRect().top - getAppViewport().top - MESSAGE_SCROLL_TOP_OFFSET) <= MESSAGE_NAV_TOLERANCE;
        const targetUserMessagePosition = isAtCurrentMessageTop && currentUserMessagePosition > 0 ? currentUserMessagePosition - 1 : currentUserMessagePosition;

        if (targetUserMessagePosition === 0) {
            // Include the scrolling logo and safe-area spacing above the first question.
            scrollAppTo({ top: 0, behavior: 'smooth' });
            return;
        }

        scrollMessageIntoView(userMessageIndexes[targetUserMessagePosition]);
    }, [getCurrentUserMessagePosition, scrollMessageIntoView, triggerContentBounce, userMessageIndexes]);

    const handleScrollToBottom = useCallback(() => {
        setTopAnchoredExploreNextRunId(null);
        setExploreNextAnchorExtraPadding(0);

        const scrollingElement = getAppScrollElement();

        if (!scrollingElement) {
            return;
        }

        // Exclude the temporary anchor spacing that this action clears.
        const maxScrollTop = Math.max(0, scrollingElement.scrollHeight - exploreNextAnchorExtraPadding - getAppViewport().height);

        if (maxScrollTop - getAppScrollTop() <= BOTTOM_SCROLL_TOLERANCE) {
            triggerContentBounce('bottom');
            return;
        }

        scrollAppTo({ top: maxScrollTop, behavior: 'smooth' });
    }, [exploreNextAnchorExtraPadding, triggerContentBounce]);

    const exploreNextDisplayState = useMemo(() => {
        const exploreNextGroups = displayedMessages.map((message, index) => {
            if (message.role !== 'assistant') {
                return null;
            }

            return {
                groupId: `message-${index}`,
                suggestions: getVisibleChatV2ExploreNextSuggestions({
                    exploreNextSuggestions: message.exploreNextSuggestions,
                    exploreNextTopics: message.exploreNextTopics,
                    exploredTopicKeys,
                }),
            };
        }).filter(Boolean);

        if (showLiveRunMessage && !liveRunResponseIsSaved) {
            exploreNextGroups.push({
                groupId: 'live-run',
                suggestions: getVisibleChatV2ExploreNextSuggestions({
                    exploreNextSuggestions: currentRun && currentRun.exploreNextSuggestions,
                    exploreNextTopics: currentRun && currentRun.exploreNextTopics,
                    exploredTopicKeys,
                }),
            });
        }

        const visibleExploreNextGroups = exploreNextGroups.filter((group) => group.suggestions.length > 0);
        const lastExploreNextGroup = visibleExploreNextGroups.length > 0 ? visibleExploreNextGroups[visibleExploreNextGroups.length - 1] : null;
        const seenPreviousTopicKeys = new Set(lastExploreNextGroup ? lastExploreNextGroup.suggestions.map((suggestion) => normalizeChatV2TopicKey(suggestion)) : []);
        const previousSuggestions = [];

        visibleExploreNextGroups.slice(0, -1).forEach((group) => {
            group.suggestions.forEach((suggestion) => {
                const topicKey = normalizeChatV2TopicKey(suggestion);

                if (!topicKey || seenPreviousTopicKeys.has(topicKey)) {
                    return;
                }

                seenPreviousTopicKeys.add(topicKey);
                previousSuggestions.push(suggestion);
            });
        });

        const exploreNextByGroupId = {};

        exploreNextGroups.forEach((group) => {
            if (!lastExploreNextGroup || lastExploreNextGroup.groupId !== group.groupId) {
                exploreNextByGroupId[group.groupId] = {
                    suggestions: [],
                    previousSuggestions: [],
                };
                return;
            }

            exploreNextByGroupId[group.groupId] = {
                suggestions: group.suggestions,
                previousSuggestions,
            };
        });

        return {
            messageExploreNext: displayedMessages.map((message, index) => {
                if (message.role !== 'assistant') {
                    return null;
                }

                return exploreNextByGroupId[`message-${index}`] || {
                    suggestions: [],
                    previousSuggestions: [],
                };
            }),
            liveRunExploreNext: exploreNextByGroupId['live-run'] || {
                suggestions: [],
                previousSuggestions: [],
            },
        };
    }, [currentRun, displayedMessages, exploredTopicKeys, liveRunResponseIsSaved, showLiveRunMessage]);
    const liveRunHasRenderableContent = Boolean(
        currentRun &&
        (
            isChatV2RunActive(currentRun) ||
            currentRun.status === 'failed' ||
            (currentRun.progress && currentRun.progress.state && currentRun.progress.state !== 'complete') ||
            hasAssistantBodyContent(currentRun) ||
            exploreNextDisplayState.liveRunExploreNext.suggestions.length > 0 ||
            exploreNextDisplayState.liveRunExploreNext.previousSuggestions.length > 0 ||
            currentRun.error
        )
    );

    const renderAssistantMessage = useCallback((message, index) => {
        const exploreNextData = exploreNextDisplayState.messageExploreNext[index] || {
            suggestions: [],
            previousSuggestions: [],
        };
        const hasRenderableContent = (engineVersion === 3 && Boolean(message.earlyShowMatch)) || hasAssistantBodyContent(message) ||
            exploreNextData.suggestions.length > 0 ||
            exploreNextData.previousSuggestions.length > 0;

        if (!hasRenderableContent) {
            return null;
        }

        return (
            <div
                key={message.runId ? `assistant-${message.runId}` : message.id || `assistant-${index}`}
                ref={bindMessageNode(index)}
                className='chatV2AssistantTurn'
                style={{
                    scrollMarginTop: `${MESSAGE_SCROLL_TOP_OFFSET}px`,
                }}
            >
                <div className='chatThreadAssistantPanel'>
                    <ChatAssistantTurn engineVersion={engineVersion} earlyShowMatch={message.earlyShowMatch}
                        runId={message.runId} runStatus='complete' sections={message.sections}>
                        <ChatV2Components
                            assistantText={message.text}
                            sections={message.sections}
                            previewPresentationMode={engineVersion === 3 ? 'summary_only' : message.previewPresentationMode}
                            exploreNextSuggestions={exploreNextData.suggestions}
                            previousExploreNextSuggestions={exploreNextData.previousSuggestions}
                            exploreNextGroupKey={`message-${index}`}
                            isTopicAlreadyExplored={isTopicAlreadyExplored}
                            onSelectExploreNextTopic={handleExploreNextTopic}
                            exploreNextTopicsDisabled={exploreNextTopicsDisabled}
                        />
                    </ChatAssistantTurn>
                </div>
            </div>
        );
    }, [engineVersion, exploreNextDisplayState.messageExploreNext, exploreNextTopicsDisabled, handleExploreNextTopic, isTopicAlreadyExplored]);

    const renderUserMessage = (message, index) => (
        <div
            key={message.id || `${message.role}-${index}`}
            ref={bindMessageNode(index, message.runId)}
            className='chatThreadUserTurn'
            style={{ scrollMarginTop: `${MESSAGE_SCROLL_TOP_OFFSET}px` }}
        >
            <button
                type='button'
                onClick={() => {
                    void copyUserMessageToClipboard(message.text);
                }}
                className='clampSafeButton chatThreadQueryCard chatThreadQueryCardButton'
                aria-label='Copy message to clipboard'
                title='Copy message'
            >
                <div className='chatThreadQueryText chatThreadText'>{message.text}</div>
            </button>
        </div>
    );

    const bottomControls = (threadHasResponse || shouldShowMessageNavigation) && (
        <div className='chatThreadFloatingNavInner'>
            {threadHasResponse && (
                <button
                    type='button'
                    onClick={shareButtonClick}
                    disabled={sharing || unsharing}
                    aria-label={publicView ? 'Copy thread link' : 'Share thread'}
                    title={publicView ? 'Copy thread link' : 'Share thread'}
                    className='chatThreadHeaderButton chatThreadHeaderPillButton chatThreadShareButton shareChat'
                >
                    <IosShare style={{ fontSize: '18px' }} />
                    <span className='chatThreadHeaderButtonLabel'>Share</span>
                </button>
            )}
            {shouldShowMessageNavigation && (
                <div className='chatThreadScrollButtons'>
                    {canScrollUp && (
                        <button
                            type='button'
                            onClick={handleScrollToMessageTop}
                            className='chatThreadFloatingNavButton'
                            aria-label='Scroll to message top'
                        >
                            <KeyboardArrowUpRounded />
                        </button>
                    )}
                    <button
                        type='button'
                        onClick={handleScrollToBottom}
                        title={canScrollDown ? 'Scroll to bottom' : 'At bottom'}
                        className='chatThreadFloatingNavButton'
                        aria-label='Scroll to bottom'
                    >
                        <KeyboardArrowDownRounded />
                    </button>
                </div>
            )}
        </div>
    );

    const contentBodyBounceClassName = contentBounceDirection === 'top' ?
        ' chatThreadContentBodyTopBounce' :
        contentBounceDirection === 'bottom' ?
            ' chatThreadContentBodyBottomBounce' :
            '';

    return (
        <div className={`container appScreen${publicView ? ' chatThreadPublicContainer' : ''}`}>
            <div className='chatThreadPage'>
                <div className='chatThreadHeader'>
                    <button
                        type='button'
                        onClick={() => navigate('/home')}
                        aria-label='Home'
                        title='Home'
                        className='chatThreadHeaderButton chatThreadHeaderIconButton'
                    >
                        <img src='/icon.png' alt='' className='chatThreadHeaderLogo' />
                    </button>
                </div>

                <div ref={contentRef} className='chatThreadContent' style={{ padding: `0px 0px ${contentBottomPadding}` }}>
                    {publicView && bottomControls && (
                        <div ref={bottomControlsOverlayRef} className='chatThreadFloatingNav'>
                            {bottomControls}
                        </div>
                    )}

                    <div className={`chatThreadContentBody${contentBodyBounceClassName}`}>
                        {loading && displayedMessages.length === 0 ?
                            <div className={`chatThreadLoadingState${publicView ? '' : ' chatThreadLoadingStatePrivate'}`}>
                                {publicView ? <PublicChatThreadLoading /> : <ChatThreadLoading thinking={currentRunIsActive} />}
                            </div> :
                            <div className='chatThreadConversationStack'>
                                {/* One keyed list preserves each run's suggestion through publication. */}
                                {displayedMessages.map((message, index) => message.role === 'assistant'
                                    ? renderAssistantMessage(message, index) : renderUserMessage(message, index)).concat(
                                    showLiveRunMessage && liveRunHasRenderableContent ? (
                                    <div
                                        key={`${liveRunResponseIsSaved ? 'live' : 'assistant'}-${currentRun.runId}`}
                                        ref={bindMessageNode(displayedMessages.length)}
                                        className='chatV2AssistantTurn'
                                        style={{
                                            scrollMarginTop: `${MESSAGE_SCROLL_TOP_OFFSET}px`,
                                        }}
                                    >
                                        <div className='chatThreadAssistantPanel'>
                                            <ChatAssistantTurn engineVersion={engineVersion}
                                                earlyShowMatch={liveRunResponseIsSaved || (publicView && currentRunIsActive) ? null : currentRun.earlyShowMatch}
                                                runId={currentRun.runId} runStatus={currentRun.status} sections={currentRun.sections}>
                                                {({ hasEarlyShowMatch }) => <>
                                                    <ChatV2Components
                                                        progress={currentRun.progress}
                                                        useServerPlanningMessages={engineVersion === 3}
                                                        hasEarlyShowMatch={hasEarlyShowMatch}
                                                        runId={currentRun.runId}
                                                        runStatus={currentRun.status}
                                                        error={errorMessage || currentRun.error}
                                                        onEditRequest={!publicView && failedRequest ? editFailedRequest : undefined}
                                                        editRequestDisabled={composerDisabled}
                                                        assistantText={liveRunResponseIsSaved ? '' : chatRequestResponseText(currentRun)}
                                                        sections={liveRunResponseIsSaved ? [] : currentRun.sections}
                                                        previewPresentationMode={engineVersion === 3 ? 'summary_only' : currentRun.previewPresentationMode}
                                                        exploreNextSuggestions={exploreNextDisplayState.liveRunExploreNext.suggestions}
                                                        previousExploreNextSuggestions={exploreNextDisplayState.liveRunExploreNext.previousSuggestions}
                                                        exploreNextGroupKey='live-run'
                                                        isTopicAlreadyExplored={isTopicAlreadyExplored}
                                                        onSelectExploreNextTopic={handleExploreNextTopic}
                                                        exploreNextTopicsDisabled={exploreNextTopicsDisabled}
                                                    />
                                                    {!!currentRun.error && !currentRunFailed && (
                                                        <div className='chatThreadInlineError chatThreadInlineErrorInside'>
                                                            {currentRun.error}
                                                        </div>
                                                    )}
                                                </>}
                                            </ChatAssistantTurn>
                                        </div>
                                    </div>
                                ) : [])}
                            </div>}

                        {errorMessage && publicView && (
                            <div className='chatThreadInlineError'>
                                {errorMessage}
                            </div>
                        )}

                        {publicView && !loading && (
                            <div className='chatThreadPublicQuestionCta'>
                                <button
                                    type='button'
                                    onClick={() => navigate('/ask')}
                                    className='chatThreadPublicQuestionButton'
                                >
                                    <span className='chatThreadPublicQuestionButtonIcon' aria-hidden='true'>
                                        <ArrowForwardRounded style={{ fontSize: '18px' }} />
                                    </span>
                                    <span className='chatThreadPublicQuestionButtonText'>
                                        Ask something else
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {!publicView && bottomControls && (
                    <div
                        ref={bottomControlsOverlayRef}
                        className='footerNoBlur chatThreadComposerNavDock'
                        style={{ bottom: `${composerDockHeight - 20}px` }}
                    >
                        <div className='chatThreadComposerNav'>
                            {bottomControls}
                        </div>
                    </div>
                )}

                {showComposer && (
                    <div ref={composerOverlayRef} className='footerNoBlur chatThreadComposerDock'>
                        <div className='chatBoxFooter homeChatBoxFooter chatThreadComposerFooter'>
                            <div className='chatBox homeChatBox chatThreadComposerBox'>
                                <form onSubmit={submitMessage}>
                                    <div className='chatInputWrap homeChatInputWrap chatThreadComposerInputWrap'>
                                        <textarea
                                            ref={textareaRef}
                                            placeholder={currentRunFailed ? 'Edit your request' : 'Ask a follow-up'}
                                            aria-label='Your request'
                                            className='chatInput hideScrollbar homeChatInput chatThreadComposerInput'
                                            rows={1}
                                            maxLength={CHAT_V2_MAX_INPUT_CHARACTERS}
                                            value={text}
                                            onChange={handleComposerChange}
                                            onKeyDown={handleComposerKeyDown}
                                            disabled={composerDisabled}
                                        />

                                        {hasText && (
                                            <button
                                                type='submit'
                                                className='chatSubmitBtn chatSubmitBtnVisible homeChatSubmitBtn chatThreadComposerSubmit'
                                                aria-label='Send'
                                                disabled={sendDisabled}
                                                style={{ opacity: sendDisabled ? 0.6 : 1 }}
                                            >
                                                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M5 12h12m0 0-5-5m5 5-5 5"
                                                        stroke="currentColor"
                                                        strokeWidth="2.4"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {errorMessage && !currentRunFailed && (
                                <div className='chatThreadComposerMessage'>
                                    <ChatRequestFailure error={errorMessage} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
                    <DialogTitle>Share this conversation?</DialogTitle>
                    <DialogContent>
                        Anyone with the link will be able to view the shared snapshot.
                    </DialogContent>
                    <DialogActions>
                        {ownedThreadIsShared && (
                            <button
                                onClick={() => void stopSharing()}
                                disabled={sharing || unsharing}
                                style={{ padding: '8px 14px' }}
                            >
                                Stop sharing
                            </button>
                        )}
                        <button
                            onClick={() => setShareDialogOpen(false)}
                            disabled={sharing || unsharing}
                            style={{ padding: '8px 14px' }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => void createOrUpdateShareLink()}
                            disabled={sharing || unsharing}
                            style={{ padding: '8px 14px' }}
                        >
                            {ownedThreadIsShared ? 'Update & copy link' : 'Create & copy link'}
                        </button>
                    </DialogActions>
                </Dialog>

                <Dialog open={!!popupMessage} onClose={() => setPopupMessage('')}>
                    <DialogContent>
                        {popupMessage}
                    </DialogContent>
                    <DialogActions>
                        <button onClick={() => setPopupMessage('')} style={{ padding: '8px 14px' }}>
                            OK
                        </button>
                    </DialogActions>
                </Dialog>

                {!!copyNotificationMessage && (
                    <div
                        className='chatThreadCopyNotification'
                        style={{ bottom: `max(${bottomOverlayHeight + 16}px, calc(env(safe-area-inset-bottom) + 24px))` }}
                        role='status'
                        aria-live='polite'
                    >
                        {copyNotificationMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
});

const VersionedChatThread = (props) => {
    const params = useParams();
    return <ChatThreadScreen key={`${props.engineVersion || 2}:${props.uid || 'guest'}:${params.threadId || params.sharedThreadId}`} {...props} />;
};

const ConnectedChatThread = connect(mapStateToProps, {})(VersionedChatThread);
const ConnectedPublicChatThread = connect(mapStateToProps, {})(function PublicChatThreadScreen(props) {
    return <VersionedChatThread {...props} publicView />;
});

export { ConnectedPublicChatThread as PublicChatThread };
export default ConnectedChatThread;
